/**
 * This file is responsible for rendering the privacy policy page in the application.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the page content and translations for the page.
 * The page content is fetched using the GetCmsPage GraphQL query.
 *
 *
 *
 * The Help component from '@Hotels-at-Home/hah-fe-core' is used to render the privacy policy content.
 *
 * If there's an error during server-side rendering or fetching the page content, it is passed as a prop to the component and can be used to display an error message.
 *
 * The NextPage component from '~/ui/layouts/NextPage' is used to render the page layout.
 */

import React, { ReactElement } from 'react';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Help from '@Hotels-at-Home/hah-fe-core/dist/components/Help';
import { getLocalNavigationItems } from '../../../constants/helpNavigation';

import { publicQuery, GetCmsPage } from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';
import { extractErrorMessage } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';
import apolloConfig from '~/constants/apolloConfig';
import { LanguageCode } from '@Hotels-at-Home/hah-fe-core/dist/constants';

import { TServerSideProps, IHelpContent } from '@Hotels-at-Home/hah-fe-core/dist/types';

interface IPrivacyPolicy {
  pageContent: IHelpContent;
}

type TProps = TServerSideProps<IPrivacyPolicy>;

const PrivacyPolicyPage = (props: TProps): ReactElement => {
  const { error } = props;

  const { t } = useTranslation('', { keyPrefix: '' });

  const meta = {
    title: `${t('$local.$meta.meta_title')} - ${t('$common.$labels.privacyStatement')}`,
    description: `${t('$local.$meta.meta_title')} - ${t('$common.$labels.privacyStatement')}`,
  };

  return (
    <NextPage error={error} meta={meta}>
      <Help {...props} getLocalNavigationItems={getLocalNavigationItems} />
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps<TProps> = async ctx => {
  const props: TProps = {
    ...(await serverSideTranslations(ctx.locale)),
    error: null,
    pageContent: null,
  };

  try {
    props.pageContent = await publicQuery(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: GetCmsPage,
        variables: {
          locale: ctx.locale,
          name: 'privacy-policy-cookie-restriction-mode',
        },
      },
      'GetCmsPage',
      ctx,
    ).then(response => response.data.cmsPage);
  } catch (e) {
    props.error = extractErrorMessage(e);
  }

  return { props };
};

export default PrivacyPolicyPage;
